import React, { useId } from 'react';

interface PropertyIconProps {
  className?: string;
  size?: number;
}

/**
 * Property icon component
 * Displays a house icon for property entities
 */
export const PropertyIcon: React.FC<PropertyIconProps> = ({ 
  className = "", 
  size 
}) => {
  const sizeProps = size ? { width: size, height: size } : {};
  const uid = useId();
  const id = (name: string) => `${uid}-${name}`;
  
  return (
    <svg
      {...sizeProps}
      className={className}
      viewBox="0 0 512 512"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <defs>
        {/* House body gradient (tan/beige) */}
        <linearGradient id={id("house")}>
          <stop offset="0" style={{ stopColor: '#d4ba9d', stopOpacity: 1 }} />
          <stop offset="1" style={{ stopColor: '#cfab74', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          xlinkHref={`#${id("house")}`}
          id={id("house-applied")}
          x1="56.541946"
          y1="271.3049"
          x2="455.95738"
          y2="271.3049"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(-6)"
        />
        
        {/* Roof gradient (red) */}
        <linearGradient id={id("roof")}>
          <stop offset="0" style={{ stopColor: '#bf4040', stopOpacity: 1 }} />
          <stop offset="1" style={{ stopColor: '#ad3131', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          xlinkHref={`#${id("roof")}`}
          id={id("roof-applied")}
          x1="152.67092"
          y1="11.618"
          x2="531.28992"
          y2="11.618"
          gradientUnits="userSpaceOnUse"
          gradientTransform="rotate(40,-3,-8.2424322)"
        />
        
        {/* Door gradient (blue) */}
        <linearGradient id={id("door")}>
          <stop offset="0" style={{ stopColor: '#5f98c8', stopOpacity: 1 }} />
          <stop offset="1" style={{ stopColor: '#3e4ebf', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          xlinkHref={`#${id("door")}`}
          id={id("door-applied")}
          x1="196.38629"
          y1="358.74952"
          x2="331.6172"
          y2="358.74952"
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(0.9299363,0,0,0.56653994,97.04177,110.004)"
        />
        
        {/* Window gradient (gray) */}
        <linearGradient id={id("window")}>
          <stop offset="0" style={{ stopColor: '#838383', stopOpacity: 1 }} />
          <stop offset="1" style={{ stopColor: '#626262', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          xlinkHref={`#${id("window")}`}
          id={id("window-applied")}
          x1="196.38629"
          y1="358.74952"
          x2="331.6172"
          y2="358.74952"
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(1.1180478,0,0,0.91908645,-130.51417,24.441892)"
        />
        
        {/* Door frame gradient (orange/brown) */}
        <linearGradient id={id("doorframe")}>
          <stop offset="0" style={{ stopColor: '#bf842d', stopOpacity: 1 }} />
          <stop offset="1" style={{ stopColor: '#c36701', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          xlinkHref={`#${id("doorframe")}`}
          id={id("doorframe-applied")}
          x1="196.38629"
          y1="358.74952"
          x2="331.6172"
          y2="358.74952"
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(0.88535032,0,0,0.85293911,-69.081649,55.664546)"
        />
        
        {/* Tree foliage gradient (green) */}
        <linearGradient id={id("tree")}>
          <stop offset="0" style={{ stopColor: '#13c413', stopOpacity: 1 }} />
          <stop offset="1" style={{ stopColor: '#008300', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          xlinkHref={`#${id("tree")}`}
          id={id("tree-applied")}
          x1="367.79362"
          y1="231.81462"
          x2="508.19259"
          y2="231.81462"
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(1,0,0,1.1281331,0,-15.060295)"
        />
      </defs>
      <g>
        {/* Tree foliage */}
        <path
          d="m 508.19259,368.1056 c 0,125.29591 -31.42938,104.5806 -70.19949,104.58059 -38.7701,-1e-5 -70.19948,20.71531 -70.19948,-104.58059 0,-125.29591 31.42938,-349.156035 70.19948,-349.156046 38.77011,-7e-6 70.19949,223.860126 70.19949,349.156046 z"
          fill={`url(#${id("tree-applied")})`}
        />
        
        {/* House body */}
        <path
          d="m 50.541946,232.81062 199.707714,-164 199.70772,164 V 479.79918 H 50.541946 Z"
          fill={`url(#${id("house-applied")})`}
        />
        
        {/* Roof */}
        <path
          d="M 250.67968,8.7656262 3.5546832,216.12695 28.621089,256 250.67969,69.669918 472.74023,256 l 25.0664,-39.87305 z"
          fill={`url(#${id("roof-applied")})`}
        />
        
        {/* Roof shadow */}
        <path
          d="M 250.67985,69.495755 49.218297,238.54112 l 2.434663,26.50536 199.0269,-165.90025 199.02848,165.90025 2.43468,-26.50536 z"
          fill="#000000"
          fillOpacity="0.115502"
        />
        
        {/* Door */}
        <path
          d="M 279.66848,249.07985 H 405.42461 V 377.42002 H 279.66848 Z"
          fill={`url(#${id("door-applied")})`}
        />
        
        {/* Window */}
        <path
          d="M 89.05505,250.06189 H 240.24966 V 458.2656 H 89.05505 Z"
          fill={`url(#${id("window-applied")})`}
        />
        
        {/* Door frame */}
        <path
          d="M 104.78899,265.04648 H 224.51572 V 458.2656 H 104.78899 Z"
          fill={`url(#${id("doorframe-applied")})`}
        />
        
        {/* Ground */}
        <rect
          x="70.659561"
          y="458.23468"
          width="182.6048"
          height="34.453735"
          fill="#7e643c"
        />
      </g>
    </svg>
  );
};
